/**
 * Utility class for collections
 */

// Type de tri
export type WDSSortFieldWay = 'asc' | 'desc';

/**
 * Représente un sortField pour trier la dataSource
 */
export interface WDSSortField {
  property: string; // champ source
  way: WDSSortFieldWay; // ordre
  numeric?: boolean; // gestion du numérique pour l'ordre
}

/**
 * Type représentant une filter function
 */
export type WDSFilterFunction<T> = (item: T, index?: number, ar?: T[]) => boolean;

export type WDSChangeType =
  | 'init' // création
  | 'refreshed' // demande de refresh (filter / sort)
  | 'selectionChanged' // changement UTILISATEUR => trappé dans le compo pour dispatch Model_update
  | 'programmaticChanged' // changement UTILISATEUR => trappé dans le compo pour dispatch Model_update
  | 'nodeChanged' // pour Tree. Changement de la portée d'affichage
  | 'itemUpdated' // indique la mise à jour d'items
  | 'treeInit' // obligation de créer des types pour l'init du tree
  | 'treeRefreshed' // obligation de créer des types pour le refresh du tree;
  | 'orderChanged'; // Changement Utilisateur sur l'ordre de la collection

/**
 * Les différents types de changements sur la data source.
 * Ils sont émis par l'observable dispatchant les changes
 */
export type WDSChanges = {
  /** le type de changement */
  type: WDSChangeType;

  value: any; // La valeur associée au changement

  action?: WDSChangeAction; // Description supplémentaire sur le type d'action (ajout, suppression)
  item?: any; // Item de la dataSource supprimé ou ajouté (facultatif)
};

export type WDSChangeAction = 'added' | 'deleted';

/** le mode de sélection autorisé  */
export type WDSSelectionMode = 'none' | 'single' | 'multiple' | 'wiz-single'; // wiz-single force la checkbox de sélection

export interface SortField {
  property: string, // champ source
  way: 'asc' | 'desc'; // ordre
  numeric?: boolean; // gestion du numérique pour l'ordre
}

export class WizCollectionUtils {

  /**
   * <p>Convert a flat collection to a tree object.</p>
   *
   * @param pSource source collection
   * @param pClassFactory object class Kind
   * @param pSortField sort field for recurse ordering
   * @param pChildrenField children collection field. Default = children
   * @param pAddChildMethodName single Add method on object. Default = addChild
   * @return an Array of roots objects
   */
  public static arrayToTree(pSource: Array<any>,
                            pClassFactory: any ,
                            pSortField?: string,
                            pChildrenField?: string,
                            pAddChildMethodName?: string): Array<any> {

    const childrenField: string = pChildrenField ? pChildrenField : 'children';
    const addChildMethodName: string = pAddChildMethodName ? pAddChildMethodName : 'addChild';
    const sortField: string = pSortField ? pSortField : null;

    const processMap: Map<string, any> = new Map();
    const result: any[] = []; // les sans parents (la racine)
    // on boucle sur la collection à plat
    // 1) je m'ajoute au dico si je n'y suis pas. Si j'y suis, c'est un fake, je le remplace
    // mais avant, je m'empare de ses enfants
    // 2) J'ajoute mon parent s'il n'existe pas (fake). Je m'ajoute à ses enfants
    // 3) si je n'ai pas de parent, je m'ajoute au chronoRoot.
    pSource.forEach((item: any, index) => {

      // 1)
      if (!processMap.get(item.code)) { // pas dans le dico
        // rien a faire
      } else { // mon fake existe, je le remplace en adoptant ses enfants
        item[childrenField] = processMap.get(item.code)[childrenField];
      }
      processMap.set(item.code, item);

      // 2)
      if (item.parentCode) {
        if (!processMap.get(item.parentCode)) { // parent pas dans le dico
          // j'ajoute son fake
          const fakeDaddy =  new pClassFactory.prototype.constructor();
          fakeDaddy.code = item.parentCode;
          //console.log('fakeDaddy', fakeDaddy);
          processMap.set(item.parentCode, fakeDaddy);
        } else {
          // papa est présent. rien à faire
        }
        processMap.get(item.parentCode)[addChildMethodName](item); // je m'ajoute
      } else { // 3) je suis orphelin
        result.push(item);
      }

    });

    // post traitement. Si sortField, organisation des collections
    if (sortField && result) {

      // détection du type de champ sur le 1er objet
      const patient0: any = result[0];
      const sortKind: string = patient0[sortField].constructor.name;
      console.log('WARN CONSTRUCTOR', patient0[sortField].constructor.name );

      this.recurseSort(result, childrenField, sortField, sortKind);

    }
    return result;
  }

  /**
   * Application récursive du champ de tri
   * @param arr le tableau d'objets
   * @param field le champ
   */
  public static recurseSort(arr: any[], childrenField: string, sortField: string, sortKind: string): void {
    arr.forEach((item: any, index) => { // même appel pour chaque enfant qui porte une collection
      if (item[childrenField]) {
        this.recurseSort(item[childrenField], childrenField, sortField, sortKind);
      }
    });

    arr.sort( (a, b) => { // tri véritable
      if (a[sortField] && b[sortField]) {
        if (sortKind === 'string') {
          return a[sortField].localeCompare(b[sortField]);
        } else {// number
          return a[sortField] - b[sortField];
        }

      } else {
        return -1;
      }
    });
  }

  /** Default compare function between 2 values
   * @param valueA First value compared
   * @param valueB Second value compared
   * @param numeric: boolean = false
   */
  public static simpleSort(valueA: any, valueB: any, numeric:boolean = false): number {
    let result: number = 0;
    if (valueA || valueB) {
      if (valueA && valueB) {
        if (numeric) {
          result = valueA - valueB;
        } else if (valueA > valueB) {
          result = 1;
        } else if (valueA < valueB) {
          result = -1;
        }
      } else {
        result = valueA ? 1 : -1;
      }
    }
    return result;
  }

  /**
   *
   * tri générique multiFields : tranforme la collection passée
   * @param collection la collection à trier
   * @param fields les champs
   */
  public static doSort<T>(collection: T[], fields: SortField[] | WDSSortField[]): T[] {

    if (collection && collection.length && fields && fields.length) {
      return [... collection].sort((itemA: T, itemB: T) => {
        return fields.reduce((result: number, field: WDSSortField | SortField) => {
          // déjà trié par le field précédent, on garde le résultat
          if (!isNaN(result) && result !== 0) {
            return result;
          }

          const valueA: any = this.getValue(itemA, field.property);
          const valueB: any = this.getValue(itemB, field.property);



          if (!field.numeric) { // null safe sur les propriétés triées
            if (!valueA && !valueB) {
              return 0;
            } else if (!valueA && !!valueB) {
              return field.way === 'asc' ?
                      -1 : 1;
            } else if (!!valueA && !valueB) {
              return field.way === 'asc' ?
                      1 : -1;
            }
          } else {
            if (isNaN(valueA) && isNaN(valueB)) {
              return 0;
            } else if (isNaN(valueA) && !isNaN(valueB)) {
              return field.way === 'asc' ?
                      -1 : 1;
            } else if (!isNaN(valueA) && isNaN(valueB)) {
              return field.way === 'asc' ?
                      1 : -1;
            }
          }

          // gestion du tri en mode numérique
          if (field.numeric) {
            return field.way === 'asc' ? valueA - valueB : valueB - valueA;
          } else {
            try {

              return field.way === 'asc' ?
              String(valueA).localeCompare(String(valueB)) :
              String(valueB).localeCompare(String(valueA))
            } catch(error) {
                console.log('null field on any', field.property );
            }

          }
        }, NaN);
      });
    }
    return collection;
  }

  /**
   * Retourne la value dans l'objet au path donnée pour les paths de type "prop.prop.prop..."
   */
  public static getValue(pObject: any, pPath: string): any {
    const props: string[] = pPath.split('.');

    if (props.length === 1) {
      return pObject[pPath];
    } else {
      let result: any = pObject[props.shift()];
      for (let index = 0; index < props.length; index++) {
        const prop: string = props[index];
        result = result[prop];
      }
      return result;
    }
  }

  public static strMapToObj(strMap) {
    let obj = Object.create(null);
    for (let [k,v] of strMap) {
      // We don’t escape the key '__proto__'
      // which can cause problems on older engines
      obj[k] = v;
    }
    return obj;
  }

  public static objToStrMap(obj) {
    let strMap = new Map();
    for (let k of Object.keys(obj)) {
      strMap.set(k, obj[k]);
    }
    return strMap;
  }

}
